/**
 * Which implementation the console runs against, decided by the environment (wf-12 U-10).
 *
 * `VITE_API_BASE_URL` unset yields the mock; set yields HTTP. The mock stays deliberately —
 * it is the demo path and the test path, seeded from the published fixtures — and the one
 * thing it must never be is loaded when a base URL is configured. So it is reached by a
 * DYNAMIC import: Vite splits it into its own chunk, a console pointed at console-api never
 * fetches it, and E-06 ("the mock module is never loaded") is a property of the build rather
 * than a promise in a comment. `loadMock` is injectable for the same reason a spy that was
 * never called is the only available proof of a module that was never loaded.
 */
import { createHttpFleetApi, type FetchLike, type TokenSource } from './http-fleet-api.ts'
import type { FleetApi } from './fleet-api.ts'

export type MockLoader = () => Promise<FleetApi>

export interface CreateFleetApiOptions {
  /** Defaults to `configuredBaseUrl()`. Pass `null` to force the mock. */
  baseUrl?: string | null
  getToken?: TokenSource
  onUnauthorized?: () => void
  fetchImpl?: FetchLike
  loadMock?: MockLoader
}

/**
 * The configured base URL, or null.
 *
 * Blank counts as unset. A `.env` carrying `VITE_API_BASE_URL=` is the shape of a variable
 * somebody meant to fill in, and treating the empty string as a base URL would produce a
 * console making relative calls into its own dev server and rendering the 404s as an empty
 * fleet.
 */
export function configuredBaseUrl(): string | null {
  const raw = import.meta.env?.VITE_API_BASE_URL
  const value = typeof raw === 'string' ? raw.trim().replace(/\/+$/, '') : ''
  return value === '' ? null : value
}

export function selectImplementation(baseUrl: string | null | undefined): 'mock' | 'http' {
  return baseUrl && baseUrl.trim() !== '' ? 'http' : 'mock'
}

/**
 * The default loader. A `.ts` extension in a dynamic import is what the rest of this repo
 * writes, and `allowImportingTsExtensions` plus Vite's resolver is what makes it work.
 */
const importMock: MockLoader = async () => {
  const module = await import('../screens/fleet/api/mock-fleet-api.ts')
  return module.mockFleetApi
}

/**
 * No token source is not a development bypass: it is a client that will refuse its first
 * call with a re-authentication signal. The console has no unauthenticated mode (U-05 says
 * so on the server side, and it would be a strange thing to enforce only there).
 */
const NO_TOKEN: TokenSource = () => null

export async function createFleetApi(options: CreateFleetApiOptions = {}): Promise<FleetApi> {
  const baseUrl = options.baseUrl === undefined ? configuredBaseUrl() : options.baseUrl
  if (selectImplementation(baseUrl) === 'mock') {
    return (options.loadMock ?? importMock)()
  }
  return createHttpFleetApi({
    baseUrl: baseUrl as string,
    getToken: options.getToken ?? NO_TOKEN,
    onUnauthorized: options.onUnauthorized,
    fetchImpl: options.fetchImpl,
  })
}
